import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import { useQuery } from '@sopranodesign/pulse-hooks';
import {
  Button,
  EmptyStatePlaceholder,
  Flex,
  Grid,
  Heading1,
  Panel,
  Span
} from '@sopranodesign/pulse-components';

import { getSharedDashboard } from '../Dashboard/api/index';
import SharedMetric from './SharedMetric';

const formatDate = date => dayjs(date).format('D MMM YYYY HH:mm');

const printStyles = `
  @media print {
    @page { margin: 12mm; }
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; background: #fff; }
    [data-print-hide] { display: none !important; }
    [data-print-avoid-break] { break-inside: avoid; page-break-inside: avoid; }
  }
`;

// The page HTML must stay out of search indexes and must not leak its token-bearing URL in Referer.
const useHiddenFromCrawlersAndReferrers = () => {
  useEffect(() => {
    const robots = document.createElement('meta');
    robots.name = 'robots';
    robots.content = 'noindex, nofollow';

    const referrer = document.createElement('meta');
    referrer.name = 'referrer';
    referrer.content = 'no-referrer';

    document.head.append(robots, referrer);

    return () => {
      robots.remove();
      referrer.remove();
    };
  }, []);
};

const SharedDashboard = ({ match }) => {
  const { token } = match.params;

  useHiddenFromCrawlersAndReferrers();

  const [settled, setSettled] = useState([]);

  const handleSettled = useCallback(
    id => setSettled(ids => (ids.includes(id) ? ids : [...ids, id])),
    []
  );

  const dashboard = useQuery({
    queryKey: ['shared-dashboard', token],
    queryFn: () => getSharedDashboard(token),
    staleTime: Infinity
  });

  if (dashboard.isError) {
    // Unknown, expired and revoked links are deliberately indistinguishable.
    return (
      <Flex col middle center yScreen pad>
        <Panel>
          <EmptyStatePlaceholder
            type="dashboard"
            heading="This link is no longer available"
            text="It may have expired or been revoked. Please contact the person who shared it with you."
          />
        </Panel>
      </Flex>
    );
  }

  if (!dashboard.data) {
    return (
      <Flex col pad ySpace aria-busy="true">
        <Grid columns={3}>
          <Panel loading loadingItems={1} />
          <Panel loading loadingItems={1} />
          <Panel loading loadingItems={1} />
        </Grid>
      </Flex>
    );
  }

  const { start, end, compare, generated_at, metrics } = dashboard.data.data;

  const ready = settled.length >= metrics.length;

  return (
    <Flex col pad ySpace data-testid="shared-dashboard">
      <style>{printStyles}</style>
      <Flex between center>
        <Flex col>
          <Heading1>Dashboard</Heading1>
          <Span light>
            {formatDate(start)} - {formatDate(end)}
            {compare ? ' (compared with the previous period)' : ''}
          </Span>
          <Span light tinyText>
            Generated {formatDate(generated_at)}
          </Span>
        </Flex>
        <div data-print-hide>
          <Button
            variant="primary"
            icon="download"
            disabled={!ready}
            onClick={() => window.print()}>
            Print / Save as PDF
          </Button>
        </div>
      </Flex>
      {metrics.length === 0 && (
        <Panel>
          <EmptyStatePlaceholder
            type="dashboard"
            heading="Nothing to show"
            text="This dashboard doesn't have any metrics."
          />
        </Panel>
      )}
      {metrics.length !== 0 && (
        <Grid columns={6}>
          {metrics.map(metric => (
            <SharedMetric
              key={metric.id}
              token={token}
              metric={metric}
              start={start}
              end={end}
              compare={compare}
              onSettled={handleSettled}
            />
          ))}
        </Grid>
      )}
    </Flex>
  );
};

SharedDashboard.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({ token: PropTypes.string.isRequired }).isRequired
  }).isRequired
};

export default SharedDashboard;
